import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { useLocation } from "react-router"
import type { Location } from "react-router"

import { getScrollRestorationKey } from "./locationKeys"

// Scroll positions outlive the page's bounded cache of photos and results.
const positions = new Map<string, number>()
const readyEntries = new Set<string>()
const storagePrefix = "prijslijst:deferred-scroll:"

function savePosition(entryId: string) {
  const position = window.scrollY
  positions.set(entryId, position)
  try {
    sessionStorage.setItem(`${storagePrefix}${entryId}`, String(position))
  } catch {
    // The in-memory position still survives snapshot eviction.
  }
}

// The router asks for the outgoing key before changing the page. Capture its
// position here, since unmount cleanup may already see a shorter loading page.
export function useScrollRestorationKey() {
  const location = useLocation()
  const currentKey = useRef(location.key)
  useLayoutEffect(() => {
    currentKey.current = location.key
  }, [location.key])
  return useCallback((target: Location) => {
    const key = getScrollRestorationKey(target)
    if (target.key === currentKey.current && readyEntries.has(key)) savePosition(key)
    return key
  }, [])
}

export default function useDeferredScrollRestoration(entryId: string, ready: boolean) {
  const [savedPosition] = useState(() => {
    const cached = positions.get(entryId)
    if (cached !== undefined) return cached
    try {
      const saved = sessionStorage.getItem(`${storagePrefix}${entryId}`)
      const position = saved === null ? NaN : Number(saved)
      return Number.isFinite(position) && position >= 0 ? position : null
    } catch {
      return null
    }
  })
  const restorePending = useRef(!ready)

  useLayoutEffect(() => {
    if (!ready) {
      readyEntries.delete(entryId)
      return
    }
    readyEntries.add(entryId)
    if (restorePending.current) {
      restorePending.current = false
      if (savedPosition !== null) window.scrollTo(0, savedPosition)
    }
  }, [entryId, ready, savedPosition])

  useLayoutEffect(() => {
    const save = () => {
      // An interrupted history read must not replace the original position with
      // the loading page's clamped scroll position.
      if (readyEntries.has(entryId)) savePosition(entryId)
    }
    window.addEventListener("pagehide", save)
    return () => {
      readyEntries.delete(entryId)
      window.removeEventListener("pagehide", save)
    }
  }, [entryId])

  // Call before replacing results with a loading state, while their height and
  // the user's current scroll position are still intact.
  return useCallback(() => {
    if (readyEntries.has(entryId)) savePosition(entryId)
  }, [entryId])
}
